import Button from "@mui/material/Button";
import type {ColumnDef} from "@tanstack/react-table";
import {AutomationFlow, AutomationStatus, NodeType} from "crqlar-react-common/dist/models/automation/automation";
import {AutomationService} from "crqlar-react-common/dist/services/automation/AutomationService";
import {getErrorMessage} from "crqlar-react-common/dist/utils/http.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useSnackbar} from "notistack";
import {ReactNode, useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useMutation, useQueryClient} from "react-query";
import {useNavigate, useParams} from "react-router-dom";
import {serverTableQueryKey} from "../../../common/serverTable";
import AutomationRunDialog from "../components/AutomationRunDialog";
import {AutomationRunCell, getListManualTrigger} from "../components/AutomationListCells";
import {remindNowMessage, remindNowOutcome, remindsNow} from "../utils/remindNow";

/**
 * The lists' "Run for everyone" column: a live run, only after its confirmation dialog (the Manual Trigger card's
 * rules, read from the saved flow). `basePath` (e.g. `/marketing/automations`) builds the toast's "View run" link.
 */
export function useListManualRun(tableId: string, basePath: string): {
    runColumn: ColumnDef<AutomationFlow>;
    runDialog: ReactNode;
} {
    const {t} = useTranslation();
    const {appId} = useParams<"appId">();
    const navigate = useNavigate();
    const readOnlyUser = useReadOnly();
    const {enqueueSnackbar} = useSnackbar();
    const queryClient = useQueryClient();
    const [confirmFlow, setConfirmFlow] = useState<AutomationFlow | null>(null);

    const getDisabledReason = useCallback((automation: AutomationFlow) => {
        const trigger = getListManualTrigger(automation);
        return readOnlyUser
            ? t("You do not have permission to run this automation.")
            : automation.status !== AutomationStatus.ACTIVE
                ? t("Set the automation live before running it for everyone.")
                : !trigger || !automation.connections?.some(connection => connection.sourceNodeId === trigger.id)
                    ? t("Connect a step to this trigger before running it.")
                    : undefined;
    }, [readOnlyUser, t]);

    const {mutate: run, isLoading, variables: runningFlow} = useMutation({
        mutationFn: async (automation: AutomationFlow) => {
            const trigger = getListManualTrigger(automation);
            const disabledReason = getDisabledReason(automation);
            if (disabledReason) throw new Error(disabledReason);
            if (!automation.id || !trigger) throw new Error(t("The manual trigger is no longer available."));

            return AutomationService.runManualTrigger(automation.id, trigger.id);
        },
        onSuccess: (execution, automation) => {
            setConfirmFlow(null);
            void queryClient.invalidateQueries(serverTableQueryKey(tableId));
            // "Remind now" says how many reminders it queued; with none there is no run to view.
            const remindNow = remindNowOutcome(execution);
            enqueueSnackbar(remindNow ? remindNowMessage(remindNow, t) : t("Run queued"), {
                variant: remindNow?.reminders === 0 ? "info" : "success",
                action: execution.id && appId ? (
                    <Button color={"inherit"} size={"small"}
                            onClick={() => navigate(`${basePath}/${appId}/${automation.id}/runs/${execution.id}`)}>
                        {t("View run")}
                    </Button>
                ) : undefined
            });
        },
        onError: error => {
            enqueueSnackbar(getErrorMessage(error, t("The run could not be started")), {variant: "error"});
        }
    });

    const runColumn = useMemo<ColumnDef<AutomationFlow>>(() => ({
        id: "run",
        header: t("Run"),
        // Hidden, never disabled (AUTOMATIONS_UI.md, Decisions), unless live with a manual trigger, a step behind it and write access.
        cell: ({row}) => getListManualTrigger(row.original) && !getDisabledReason(row.original) ? (
            <AutomationRunCell
                isRunning={isLoading && runningFlow?.id === row.original.id}
                disabled={isLoading}
                onRequestRun={() => setConfirmFlow(row.original)}
            />
        ) : null,
        size: 180,
        meta: {defaultActive: true},
    }), [t, getDisabledReason, isLoading, runningFlow]);

    const runDialog = (
        <AutomationRunDialog
            open={!!confirmFlow}
            automationName={confirmFlow?.name ?? ""}
            triggerName={(confirmFlow && getListManualTrigger(confirmFlow)?.label) || t("Manual Trigger")}
            remindNow={!!confirmFlow && remindsNow(NodeType.MANUAL_TRIGGER, confirmFlow.nodes?.map(node => node.type) ?? [])}
            hasUnsavedChanges={false}
            isRunning={isLoading}
            disabledReason={confirmFlow ? getDisabledReason(confirmFlow) : undefined}
            onClose={() => {if (!isLoading) setConfirmFlow(null);}}
            onConfirm={() => {if (confirmFlow && !isLoading) run(confirmFlow);}}
        />
    );

    return {runColumn, runDialog};
}
